/**
 * Generic CSV export helper for TanStack DataTable rows.
 *
 * Caller supplies the rows (typically
 * `table.getFilteredRowModel().rows.map(r => r.original)` or any other plain
 * array) plus the column definitions describing how to serialize each field.
 */

import { needsFormulaGuard } from './csvFormulaGuard';

export interface CsvExportColumn<T> {
  /** Header text that will appear in row 1 of the CSV. */
  header: string;
  /**
   * Function that returns the value for a given row. Return `null` or
   * `undefined` to emit an empty cell.
   */
  accessor: (row: T) => unknown;
}

/**
 * Serialize an array of rows to a CSV string using the supplied column definitions.
 *
 * - Values are coerced to string with `String(value)`.
 * - `null` and `undefined` become empty cells.
 * - Cells that a spreadsheet would interpret as a formula are prefixed with a
 *   single quote (see `needsFormulaGuard`) before quoting, so attacker-influenced
 *   strings can't execute in Excel / Google Sheets.
 * - Cells containing `,`, `"`, `\n`, or `\r` are wrapped in double quotes;
 *   embedded `"` characters are doubled per RFC 4180.
 * - The output uses CRLF line endings (also per RFC 4180).
 */
export function rowsToCsv<T>(rows: T[], columns: CsvExportColumn<T>[]): string {
  const escape = (raw: unknown): string => {
    if (raw === null || raw === undefined) return '';
    let value = String(raw);
    if (needsFormulaGuard(value)) {
      value = `'${value}`;
    }
    if (/[",\r\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const headerLine = columns.map((c) => escape(c.header)).join(',');
  const dataLines = rows.map((row) =>
    columns.map((c) => escape(c.accessor(row))).join(',')
  );

  return [headerLine, ...dataLines].join('\r\n');
}

/**
 * Trigger a CSV download in the browser. Wraps `rowsToCsv` + `Blob` + an anchor click.
 *
 * @param rows - data rows
 * @param columns - column definitions
 * @param filename - desired filename (without extension is fine; `.csv` is appended if missing)
 */
export function downloadCsv<T>(
  rows: T[],
  columns: CsvExportColumn<T>[],
  filename: string
): void {
  const csv = rowsToCsv(rows, columns);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
